import { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { FieldError, SubmitHandler } from 'react-hook-form';
import { toast } from 'react-toastify';
import z from 'zod';

import { StepInstanceStatus as GqlStepInstanceStatus } from '@willow/graphql-iso/src/app';
import { HydratedStepInstance, StepType } from '@willow/shared-iso';
import { Form as FormDefinition, schemaFromForm, Validator } from '@willow/types-iso';

import { useZodHookForm } from '../../hooks/useZodHookForm';
import { FormElement } from './FormElement';
import { WorkflowV2FormActions } from './WorkflowV2FormActions';

export interface WorkflowV2FormProps {
  stepInstance: HydratedStepInstance<StepType>;
  form: FormDefinition;
  onSubmit: SubmitHandler<Record<string, any>>;
  isActive: boolean;
  previouslySubmittedValues?: Record<string, unknown>;
  doSetStepStatus: (status: GqlStepInstanceStatus) => void;
  zodIssues?: z.core.$ZodIssue[];
}
export const WorkflowV2FormAndStatusControls = ({
  stepInstance,
  form,
  onSubmit,
  isActive,
  previouslySubmittedValues,
  doSetStepStatus,
  zodIssues,
}: WorkflowV2FormProps) => {
  const looseZodValidatorForForm = useMemo(() => Validator.fromSchema(schemaFromForm(form), false), [form]);

  const { control, handleSubmit, formState, reset, setError } = useZodHookForm(looseZodValidatorForForm, {
    defaultValues: previouslySubmittedValues ? looseZodValidatorForForm.parse(previouslySubmittedValues) : {},
  });

  /**
   * Copy/paste-ish from https://github.com/react-hook-form/resolvers/blob/6e8839343dcfea0fe9b874772a01f3bc551cbb5c/zod/src/zod.ts#L20C3-L63C17
   *
   * Sure would be nice if they'd export this function... :(
   */
  useEffect(() => {
    if (zodIssues) {
      const fieldErrors: [string, FieldError][] = zodIssues.map((error) => {
        const { code, message, path } = error;
        const joinedPath = path.join('.');

        // TODO: fix this
        // if ('unionErrors' in error) {
        //   const unionError = error.unionErrors[0].issues[0];

        //   return [
        //     joinedPath,
        //     {
        //       message: unionError.message,
        //       type: unionError.code,
        //     },
        //   ];
        // }

        return [joinedPath, { message, type: code }];
      });

      // just 1 per field is fine for now so we reduce to a map accmulating one
      // unique error per path
      const fieldErrorsMap = fieldErrors.reduce((acc, [path, error]) => {
        if (!acc.get(path)) {
          acc.set(path, error);
        }
        return acc;
      }, new Map<string, FieldError>());

      for (const [path, error] of fieldErrorsMap.entries()) {
        setError(path, { message: error.message, type: error.type });
      }
    }
  }, [zodIssues, setError]);

  useEffect(() => {
    if (previouslySubmittedValues) {
      reset(looseZodValidatorForForm.parse(previouslySubmittedValues));
    }
  }, [previouslySubmittedValues, reset, looseZodValidatorForForm]);

  // weirdly formState.isDirty is coming out true in unexpected scenarios (when
  // formState.dirtyFields is empty) so... I guess we just check if it's empty?
  const isDirty = JSON.stringify(formState.dirtyFields) !== '{}';

  const [showConfirmationModalStatus, setShowConfirmationModalStatus] = useState<GqlStepInstanceStatus | undefined>(
    undefined,
  );

  const submitAndSetStatus = async (status: GqlStepInstanceStatus) => {
    try {
      await handleSubmit(async (...args) => {
        await onSubmit(...args);
        doSetStepStatus(status);
        reset({}, { keepDirtyValues: true });
      })();
    } catch (error) {
      toast.error(`Failed to submit form for step ${stepInstance.id}: ${error}`);
    }
  };

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      await handleSubmit(async (...args) => {
        await onSubmit(...args);
        reset({}, { keepDirtyValues: true });
      })(e);
    } catch (error) {
      toast.error(`Failed to submit form for step ${stepInstance.id}: ${error}`);
    }
  };

  const setStatusWithoutSubmitting = (status: GqlStepInstanceStatus) => {
    if (isDirty) {
      setShowConfirmationModalStatus(status);
    } else {
      doSetStepStatus(status);
    }
  };

  const submitButtonText = 'Save Form';

  return (
    <>
      <Modal show={!!showConfirmationModalStatus} onHide={() => setShowConfirmationModalStatus(undefined)}>
        <Modal.Header>
          <Modal.Title>Confirm Step Status Change</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          There are unsaved changes in the step form. Are you sure you want to set the step status to{' '}
          {showConfirmationModalStatus} before saving?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmationModalStatus(undefined)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              doSetStepStatus(showConfirmationModalStatus!);
              setShowConfirmationModalStatus(undefined);
            }}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      <Form onSubmit={submit} className={!isActive ? 'workflow__form--disabled' : ''}>
        {form.map(({ key: fieldName, element }) => {
          return (
            <div className="mb-2" key={fieldName}>
              <FormElement
                fieldName={fieldName}
                element={element}
                control={control}
                disabled={!isActive}
                stepInstanceId={stepInstance.id}
              />
            </div>
          );
        })}
        <WorkflowV2FormActions
          stepInstance={stepInstance}
          submitButtonText={submitButtonText}
          setStatusWithoutSubmitting={setStatusWithoutSubmitting}
          submitAndSetStatus={submitAndSetStatus}
        />
      </Form>
    </>
  );
};
